import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner, Alert, Modal, Button, Badge } from 'react-bootstrap';
import Select from 'react-select';
import {
  FaSync, FaCopy, FaSave, FaLink, FaUsers, FaTrash, FaExternalLinkAlt, FaChevronDown, FaChevronRight,
} from 'react-icons/fa';
import api from '../../services/api';
import { defaultSelectStyles } from '../../utils/selectStyles';
import { canAccess } from '../../utils/canAccess';
import ConfirmModal from '../../components/ConfirmModal';
import InfoTip from '../../components/InfoTip';
import RapportView from './RapportView';
import RapportViewTechnique from './RapportViewTechnique';
import {
  DECOUPAGES, FORMATS, BLOCS, TYPES_RAPPORT, WHATSAPP_LIMIT,
  buildTexte, lienRapport, fmtDateFr, COMPARAISONS,
} from './format';
import { DECOUPAGES_T, BLOCS_T, TYPES_T, buildTexteTechnique, COMPARAISONS_T } from './formatTechnique';

const BROWN = '#6d4e36';
const LS_KEY = 'point_jour_reglages';

// Les deux vues du Point du jour : commercial (Décisionnel) et technique (Dashboard)
const VUES = {
  commercial: {
    label: '📊 Commercial', decoupages: DECOUPAGES, blocs: BLOCS, types: TYPES_RAPPORT, buildTexte,
    endpoint: '/decisionnel/point-jour', typePartage: 'point_jour', titre: 'Point du',
    comparaisons: COMPARAISONS, comparaisonInfo: 'Période comparée au mois en cours (du 1er au jour de référence).',
  },
  technique: {
    label: '🛠 Technique', decoupages: DECOUPAGES_T, blocs: BLOCS_T, types: TYPES_T, buildTexte: buildTexteTechnique,
    endpoint: '/dashboard/point-jour', typePartage: 'point_technique', titre: 'Point technique du',
    comparaisons: COMPARAISONS_T, comparaisonInfo: 'Période comparée à la période de paie en cours (du 21 au jour de référence).',
  },
};
export const TYPE_LABELS = { point_jour: 'Commercial', point_technique: 'Technique' };

const pad2 = (n) => String(n).padStart(2, '0');
const hier = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const loadReglages = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null') || {}; } catch { return {}; }
};
// Réglages d'une vue (les anciens réglages à plat sont ceux de la vue commerciale)
const reglagesVue = (saved, vue) => {
  const src = saved[vue] || (vue === 'commercial' ? saved : {});
  const v = VUES[vue];
  return {
    typeRapport: src.typeRapport || 'quotidien',
    decoupage: v.decoupages.some(d => d.value === src.decoupage) ? src.decoupage : 'global',
    format: src.format || 'standard',
    blocs: Array.isArray(src.blocs) ? src.blocs : v.types[0].blocs,
    comparaison: v.comparaisons.some(c => c.value === src.comparaison) ? src.comparaison : v.comparaisons[0].value,
    compDates: src.compDates || { start: '', end: '' },
  };
};

const Toggle = ({ options, value, onChange }) => (
  <div className="btn-group btn-group-sm flex-wrap" role="group">
    {options.map(o => (
      <button key={o.value} type="button"
        className={`btn ${value === o.value ? 'btn-marron' : 'btn-outline-secondary'}`}
        onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

// Panneau repliable : replié, seul l'en-tête reste visible (laisse la place aux chiffres de contrôle)
const Panneau = ({ titre, ouvert, onToggle, extra, couleur, style, children }) => (
  <div className="p-3 h-100" style={{ borderRadius: 10, ...style }}>
    <div className="d-flex justify-content-between align-items-center gap-2" style={{ marginBottom: ouvert ? 8 : 0 }}>
      <button type="button" className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2"
        style={{ color: couleur, fontWeight: 700 }} onClick={onToggle} title={ouvert ? 'Réduire' : 'Agrandir'}>
        {ouvert ? <FaChevronDown /> : <FaChevronRight />} {titre}
      </button>
      {extra}
    </div>
    {ouvert && children}
  </div>
);

// Largeur d'un panneau selon son état et celui de son voisin
const colClass = (ouvert, voisinOuvert, largeurOuvert) => {
  if (!ouvert) return 'col-12 col-md-auto';
  return voisinOuvert ? `col-12 ${largeurOuvert}` : 'col-12 col-md';
};

const copier = async (txt) => {
  try { await navigator.clipboard.writeText(txt); return true; } catch { return false; }
};

/* ─── Modal lecteurs (création du rapport ou modification) ─────────────────── */
const LecteursSelect = ({ users, value, onChange }) => (
  <Select
    isMulti
    styles={defaultSelectStyles}
    menuPortalTarget={document.body}
    placeholder="Ajouter un utilisateur…"
    options={users.map(u => ({ value: u.id, label: u.name }))}
    value={users.filter(u => value.includes(u.id)).map(u => ({ value: u.id, label: u.name }))}
    onChange={(opts) => onChange((opts || []).map(o => o.value))}
    noOptionsMessage={() => 'Aucun utilisateur'}
  />
);

const PointJourPage = ({ user }) => {
  const isAdmin = user?.role === 'admin';
  const saved = useMemo(loadReglages, []);
  const [searchParams, setSearchParams] = useSearchParams();

  // Vues autorisées : commercial = Décisionnel, technique = Dashboard
  const vuesAutorisees = useMemo(() => [
    (canAccess(user, 'decisionnel') || user?.role === 'resp_commercial') && 'commercial',
    canAccess(user, 'dashboard') && 'technique',
  ].filter(Boolean), [user]);
  const vueDemandee = searchParams.get('vue') || saved.vue;
  const [vue, setVue] = useState(vuesAutorisees.includes(vueDemandee) ? vueDemandee : vuesAutorisees[0]);
  const V = VUES[vue] || VUES.commercial; // vue absente (aucun accès) : page bloquée plus bas

  const [date, setDate] = useState(hier());
  const [reglages, setReglages] = useState(() => ({
    commercial: reglagesVue(saved, 'commercial'), technique: reglagesVue(saved, 'technique'),
  }));
  const { typeRapport, decoupage, format, blocs, comparaison, compDates } = reglages[vue] || reglages.commercial;
  // Dates de comparaison envoyées au serveur (null = période par défaut du calcul)
  const comp = useMemo(() => {
    if (comparaison === 'perso') return compDates.start && compDates.end && compDates.start <= compDates.end ? compDates : null;
    const c = V.comparaisons.find(x => x.value === comparaison);
    return c?.calc ? c.calc(date) : null;
  }, [V, comparaison, compDates, date]);
  const majReglages = (patch) => setReglages(r => ({ ...r, [vue]: { ...r[vue], ...(typeof patch === 'function' ? patch(r[vue]) : patch) } }));

  // Filtres commerciaux (ville / projets) et techniques (société / chantiers)
  const [villeId, setVilleId]         = useState(saved.villeId || null);
  const [projetIds, setProjetIds]     = useState(saved.projetIds || []);
  const [societe, setSociete]         = useState(saved.societe || null);
  const [chantierIds, setChantierIds] = useState(saved.chantierIds || []);
  const [blocsOuvert, setBlocsOuvert]   = useState(saved.blocsOuvert ?? true);
  const [apercuOuvert, setApercuOuvert] = useState(saved.apercuOuvert ?? true);

  const [filters, setFilters]     = useState({ villes: [], projets: [] });
  const [chantiers, setChantiers] = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [flash, setFlash]     = useState('');

  const [users, setUsers]       = useState([]);
  const [rapports, setRapports] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ titre: '', lecteurs: [] });
  const [saving, setSaving]     = useState(false);
  const [dernierToken, setDernierToken] = useState(null);
  const [editLecteurs, setEditLecteurs] = useState(null); // { id, titre, lecteurs: [ids] }
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        vue, ...reglages, villeId, projetIds, societe, chantierIds, blocsOuvert, apercuOuvert,
      }));
    } catch { /* stockage indisponible */ }
  }, [vue, reglages, villeId, projetIds, societe, chantierIds, blocsOuvert, apercuOuvert]);

  useEffect(() => {
    if (vuesAutorisees.includes('commercial')) {
      api.get('/decisionnel/filters')
        .then(r => setFilters({ villes: r.data.villes || [], projets: r.data.projets || [] }))
        .catch(() => {});
    }
    if (vuesAutorisees.includes('technique')) {
      api.get('/chantiers').then(r => setChantiers(r.data || [])).catch(() => {});
    }
  }, [vuesAutorisees]);

  const chargerRapports = useCallback(() => {
    if (!isAdmin) return;
    api.get('/rapports-partages').then(r => setRapports(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users/users-simple').then(r => setUsers(r.data || [])).catch(() => {});
    chargerRapports();
  }, [isAdmin, chargerRapports]);

  // Périmètre effectif : sélection explicite, sinon tous les projets de la ville / chantiers de la société
  const projetsEffectifs = useMemo(() => {
    if (projetIds.length) return projetIds;
    if (villeId) return filters.projets.filter(p => Number(p.cityId) === Number(villeId)).map(p => p.id);
    return [];
  }, [projetIds, villeId, filters.projets]);
  const chantiersEffectifs = useMemo(() => {
    if (chantierIds.length) return chantierIds;
    if (societe) return chantiers.filter(c => (c.societe || 'Non renseignée') === societe).map(c => c.id);
    return [];
  }, [chantierIds, societe, chantiers]);

  const charger = useCallback(() => {
    if (!vue) return;
    setLoading(true);
    setError('');
    const params = {
      date, compStart: comp?.start, compEnd: comp?.end,
      ...(vue === 'technique'
        ? { chantierIds: chantiersEffectifs.join(',') || undefined }
        : { projetIds: projetsEffectifs.join(',') || undefined }),
    };
    api.get(VUES[vue].endpoint, { params })
      .then(r => { setData(r.data); setDernierToken(null); })
      .catch(e => setError(e?.response?.data?.message || 'Erreur lors du chargement'))
      .finally(() => setLoading(false));
  }, [vue, date, projetsEffectifs, chantiersEffectifs, comp]);

  useEffect(() => { charger(); }, [charger]);

  const changerVue = (v) => {
    if (v === vue) return;
    setData(null);
    setVue(v);
    setSearchParams({ vue: v }, { replace: true });
  };

  // Données de la vue courante seulement (évite un rendu avec les données de l'autre vue)
  const dataVue = data && ((vue === 'technique') === (data.type === 'technique')) ? data : null;
  const texte = useMemo(() => (dataVue ? V.buildTexte(dataVue, { decoupage, format, blocs }) : ''), [dataVue, V, decoupage, format, blocs]);
  const pied = dernierToken ? `\n\n👉 Détail : ${lienRapport(dernierToken)}` : '\n\n👉 Détail : (lien créé à l\'enregistrement)';
  const longueur = texte.length + pied.length;

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const choisirType = (value) => {
    const t = V.types.find(x => x.value === value);
    majReglages(t ? { typeRapport: value, blocs: t.blocs } : { typeRapport: value });
  };
  const toggleBloc = (id) => majReglages(r => ({
    typeRapport: 'perso', blocs: r.blocs.includes(id) ? r.blocs.filter(x => x !== id) : [...r.blocs, id],
  }));
  const toggleGroupe = (g) => majReglages(r => {
    const ids = g.items.map(i => i.id);
    const tous = ids.every(id => r.blocs.includes(id));
    return { typeRapport: 'perso', blocs: tous ? r.blocs.filter(x => !ids.includes(x)) : [...new Set([...r.blocs, ...ids])] };
  });

  const ouvrirSave = () => {
    const dec = V.decoupages.find(d => d.value === decoupage)?.label.toLowerCase();
    setSaveForm({ titre: `${V.titre} ${fmtDateFr(dataVue?.date || date)} — ${dec}`, lecteurs: [] });
    setSaveOpen(true);
  };

  const enregistrer = async () => {
    setSaving(true);
    try {
      const r = await api.post('/rapports-partages', {
        type: V.typePartage,
        titre: saveForm.titre,
        dateRef: dataVue.date,
        texte,
        parametres: {
          typeRapport, decoupage, format, blocs, comparaison, compStart: comp?.start, compEnd: comp?.end,
          ...(vue === 'technique'
            ? { societe, chantierIds: chantiersEffectifs }
            : { villeId, projetIds: projetsEffectifs }),
        },
        lecteurs: saveForm.lecteurs,
      });
      setDernierToken(r.data.token);
      setSaveOpen(false);
      chargerRapports();
      const ok = await copier(lienRapport(r.data.token));
      showFlash(ok ? '✅ Rapport enregistré — lien copié' : '✅ Rapport enregistré');
    } catch (e) {
      setError(e?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const majLecteurs = async () => {
    try {
      await api.put(`/rapports-partages/${editLecteurs.id}/lecteurs`, { userIds: editLecteurs.lecteurs });
      setEditLecteurs(null);
      chargerRapports();
      showFlash('✅ Lecteurs mis à jour');
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const supprimer = async () => {
    try {
      await api.delete(`/rapports-partages/${toDelete.id}`);
      setToDelete(null);
      chargerRapports();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  if (!vue) return <div className="container py-4"><Alert variant="warning">Accès non autorisé.</Alert></div>;

  const villeOptions  = filters.villes.map(v => ({ value: v.id, label: v.name }));
  const projetOptions = filters.projets
    .filter(p => !villeId || Number(p.cityId) === Number(villeId))
    .map(p => ({ value: p.id, label: p.name }));
  const societeOptions = [...new Set(chantiers.map(c => c.societe || 'Non renseignée'))].sort()
    .map(s => ({ value: s, label: s }));
  const chantierOptions = chantiers
    .filter(c => !societe || (c.societe || 'Non renseignée') === societe)
    .map(c => ({ value: c.id, label: c.name }));

  return (
    <div className="container-fluid px-3 px-md-4 pb-5">
      {/* En-tête */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 my-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>
            📰 Point du jour <InfoTip text="Résumé quotidien pour la direction. Choisissez le découpage, le format et les blocs : l'aperçu montre le message tel qu'il sera envoyé. « Enregistrer » fige les chiffres et crée un lien à partager." />
          </h4>
          {vuesAutorisees.length > 1 && (
            <Toggle options={vuesAutorisees.map(v => ({ value: v, label: VUES[v].label }))} value={vue} onChange={changerVue} />
          )}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={charger} disabled={loading}><FaSync /> Actualiser</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={!dataVue}
            onClick={async () => showFlash((await copier(texte + (dernierToken ? pied : ''))) ? '✅ Texte copié' : '❌ Copie impossible')}>
            <FaCopy /> Copier le texte
          </button>
          {isAdmin && (
            <button className="btn btn-sm btn-marron" disabled={!dataVue || loading} onClick={ouvrirSave}><FaSave /> Enregistrer le rapport</button>
          )}
        </div>
      </div>

      {flash && <Alert variant="success" className="py-2">{flash}</Alert>}
      {error && <Alert variant="danger" className="py-2" onClose={() => setError('')} dismissible>{error}</Alert>}

      {/* Filtres */}
      <div className="p-3 mb-3" style={{ background: '#f9f6ef', borderRadius: 10, border: '1px solid #e8dcc8' }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label small mb-1">
              Jour de référence {vue === 'technique' && <InfoTip text="La période de paie va du 21 du mois (ou du mois précédent) jusqu'à ce jour, comme le dashboard." />}
            </label>
            <input type="date" className="form-control form-control-sm" value={date}
              onChange={e => e.target.value && setDate(e.target.value)} />
          </div>
          {vue === 'technique' ? (
            <>
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Société</label>
                <Select isClearable styles={defaultSelectStyles} placeholder="Toutes" options={societeOptions}
                  value={societeOptions.find(o => o.value === societe) || null}
                  onChange={o => { setSociete(o?.value || null); setChantierIds([]); }} />
              </div>
              <div className="col-12 col-lg-7">
                <label className="form-label small mb-1">Chantiers</label>
                <Select isMulti styles={defaultSelectStyles} placeholder="Tous" options={chantierOptions}
                  value={chantierOptions.filter(o => chantierIds.includes(o.value))}
                  onChange={opts => setChantierIds((opts || []).map(o => o.value))} />
              </div>
            </>
          ) : (
            <>
              <div className="col-12 col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Ville</label>
                <Select isClearable styles={defaultSelectStyles} placeholder="Toutes" options={villeOptions}
                  value={villeOptions.find(o => o.value === villeId) || null}
                  onChange={o => { setVilleId(o?.value || null); setProjetIds([]); }} />
              </div>
              <div className="col-12 col-lg-7">
                <label className="form-label small mb-1">Projets</label>
                <Select isMulti styles={defaultSelectStyles} placeholder="Tous" options={projetOptions}
                  value={projetOptions.filter(o => projetIds.includes(o.value))}
                  onChange={opts => setProjetIds((opts || []).map(o => o.value))} />
              </div>
            </>
          )}
        </div>
        <div className="d-flex flex-wrap gap-3 mt-3 align-items-center">
          <div><div className="small mb-1">Type de rapport</div>
            <Toggle options={[...V.types, { value: 'perso', label: 'Personnalisé' }]} value={typeRapport} onChange={choisirType} />
          </div>
          <div><div className="small mb-1">Découpage</div>
            <Toggle options={V.decoupages} value={decoupage} onChange={v => majReglages({ decoupage: v })} />
          </div>
          <div><div className="small mb-1">Format</div>
            <Toggle options={FORMATS} value={format} onChange={v => majReglages({ format: v })} />
          </div>
          <div>
            <div className="small mb-1">Comparer à <InfoTip text={`${V.comparaisonInfo} Utilisée par le bloc « Comparaison ».`} /></div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <select className="form-select form-select-sm" style={{ width: 'auto' }} value={comparaison}
                onChange={e => majReglages({ comparaison: e.target.value })}>
                {V.comparaisons.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {comparaison === 'perso' && (
                <>
                  <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={compDates.start}
                    aria-label="Début de la période de comparaison"
                    onChange={e => majReglages(r => ({ compDates: { ...r.compDates, start: e.target.value } }))} />
                  <span className="small">→</span>
                  <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={compDates.end}
                    aria-label="Fin de la période de comparaison"
                    onChange={e => majReglages(r => ({ compDates: { ...r.compDates, end: e.target.value } }))} />
                </>
              )}
              {dataVue?.comparaison && (
                <span className="small text-muted">{fmtDateFr(dataVue.comparaison.start)} → {fmtDateFr(dataVue.comparaison.end)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Blocs */}
        <div className={colClass(blocsOuvert, apercuOuvert, 'col-md-5 col-xl-4')}>
          <Panneau titre="Blocs à inclure" ouvert={blocsOuvert} onToggle={() => setBlocsOuvert(o => !o)}
            style={{ border: '1px solid #e8dcc8' }} couleur={BROWN}
            extra={!blocsOuvert && <Badge bg="secondary">{blocs.length} cochés</Badge>}>
            {V.blocs.map(g => {
              const nb = g.items.filter(i => blocs.includes(i.id)).length;
              return (
                <div key={g.id} className="mb-2">
                  <label className="d-flex align-items-center gap-2 fw-semibold" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={nb === g.items.length}
                      ref={el => { if (el) el.indeterminate = nb > 0 && nb < g.items.length; }}
                      onChange={() => toggleGroupe(g)} />
                    {g.label}
                  </label>
                  <div className="ms-4 d-flex flex-wrap" style={{ columnGap: 12 }}>
                    {g.items.map(i => (
                      <label key={i.id} className="small d-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={blocs.includes(i.id)} onChange={() => toggleBloc(i.id)} />
                        {i.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </Panneau>
        </div>

        {/* Aperçu WhatsApp */}
        <div className={colClass(apercuOuvert, blocsOuvert, 'col-md-7 col-xl-8')}>
          <Panneau titre="Aperçu WhatsApp" ouvert={apercuOuvert} onToggle={() => setApercuOuvert(o => !o)}
            style={{ background: '#e5ddd5' }} couleur="#075e54"
            extra={(
              <Badge bg={longueur > WHATSAPP_LIMIT ? 'danger' : 'success'}>
                {longueur} / {WHATSAPP_LIMIT} car.{longueur > WHATSAPP_LIMIT ? ` → ${Math.ceil(longueur / WHATSAPP_LIMIT)} messages` : ' ✓'}
              </Badge>
            )}>
            {loading && !dataVue ? (
              <div className="text-center py-5"><Spinner animation="border" size="sm" /> Chargement…</div>
            ) : (
              <div style={{
                background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '10px 12px', whiteSpace: 'pre-wrap',
                fontSize: '0.85rem', fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif', boxShadow: '0 1px 1px rgba(0,0,0,.15)',
                maxHeight: 640, overflowY: 'auto', opacity: loading ? 0.5 : 1,
              }}>
                {texte}{pied}
              </div>
            )}
            {longueur > WHATSAPP_LIMIT && (
              <div className="small mt-2 text-danger">
                Trop long pour un seul message WhatsApp : passez en format « Court », retirez des blocs ou choisissez un découpage moins fin.
              </div>
            )}
          </Panneau>
        </div>

        {/* Tableau de contrôle — pleine largeur sous les deux panneaux */}
        <div className="col-12">
          <div className="p-3" style={{ border: '1px solid #e8dcc8', borderRadius: 10, opacity: loading ? 0.5 : 1 }}>
            <div style={{ color: BROWN, fontWeight: 700 }}>
              Chiffres de contrôle <InfoTip text="Chiffres bruts servant au message, pour vérification. C'est aussi ce que verront les lecteurs du rapport enregistré." />
            </div>
            {loading && !dataVue && <div className="text-center py-4"><Spinner animation="border" size="sm" /> Chargement…</div>}
            {dataVue && (vue === 'technique' ? <RapportViewTechnique data={dataVue} /> : <RapportView data={dataVue} canLive />)}
            {!loading && !dataVue && <div className="text-muted small">Aucune donnée</div>}
          </div>
        </div>
      </div>

      {/* Rapports enregistrés (admin) */}
      {isAdmin && (
        <div className="mt-4">
          <h5 style={{ color: BROWN, fontWeight: 700 }}>
            🗂 Rapports enregistrés <InfoTip text="Chaque rapport garde les chiffres du jour où il a été enregistré. Seuls les admins et les lecteurs choisis peuvent ouvrir le lien (connexion obligatoire)." />
          </h5>
          <div className="table-container table-responsive">
            <table className="table table-bordered table-hover table-striped mb-0" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Titre</th><th>Lecteurs</th><th>Vues</th><th>Créé par</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rapports.length === 0 && <tr><td colSpan={7} className="text-muted">Aucun rapport enregistré</td></tr>}
                {rapports.map(r => (
                  <tr key={r.id}>
                    <td>{fmtDateFr(r.dateRef)}</td>
                    <td><Badge bg={r.type === 'point_technique' ? 'info' : 'secondary'}>{TYPE_LABELS[r.type] || r.type}</Badge></td>
                    <td style={{ textAlign: 'left' }}>{r.titre}</td>
                    <td>{r.lecteurs.length ? r.lecteurs.map(l => l.name).join(', ') : <span className="text-muted">Admins seulement</span>}</td>
                    <td title={r.derniereVue ? `Dernière vue : ${new Date(r.derniereVue).toLocaleString('fr-FR')}` : ''}>{r.nbVues}</td>
                    <td>{r.createdBy}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-outline-secondary me-1" title="Copier le lien"
                        onClick={async () => showFlash((await copier(lienRapport(r.token))) ? '✅ Lien copié' : '❌ Copie impossible')}><FaLink /></button>
                      <a className="btn btn-sm btn-outline-secondary me-1" title="Ouvrir" href={`/rapport/${r.token}`} target="_blank" rel="noreferrer"><FaExternalLinkAlt /></a>
                      <button className="btn btn-sm btn-outline-secondary me-1" title="Lecteurs"
                        onClick={() => setEditLecteurs({ id: r.id, titre: r.titre, lecteurs: r.lecteurs.map(l => l.id) })}><FaUsers /></button>
                      <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => setToDelete(r)}><FaTrash /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal enregistrement */}
      <Modal show={saveOpen} onHide={() => setSaveOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Enregistrer le rapport</Modal.Title></Modal.Header>
        <Modal.Body>
          <label className="form-label small">Titre</label>
          <input className="form-control mb-3" maxLength={200} value={saveForm.titre}
            onChange={e => setSaveForm(f => ({ ...f, titre: e.target.value }))} />
          <label className="form-label small">Lecteurs</label>
          <LecteursSelect users={users} value={saveForm.lecteurs} onChange={lecteurs => setSaveForm(f => ({ ...f, lecteurs }))} />
          <div className="small text-muted mt-2">ℹ️ Les admins ont toujours accès. Les chiffres sont figés au moment de l'enregistrement.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSaveOpen(false)}>Annuler</Button>
          <Button className="btn-marron" disabled={saving || !saveForm.titre.trim()} onClick={enregistrer}>
            {saving ? <Spinner animation="border" size="sm" /> : <FaSave />} Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal lecteurs */}
      <Modal show={!!editLecteurs} onHide={() => setEditLecteurs(null)} centered>
        <Modal.Header closeButton><Modal.Title>Lecteurs — {editLecteurs?.titre}</Modal.Title></Modal.Header>
        <Modal.Body>
          {editLecteurs && (
            <LecteursSelect users={users} value={editLecteurs.lecteurs}
              onChange={lecteurs => setEditLecteurs(e => ({ ...e, lecteurs }))} />
          )}
          <div className="small text-muted mt-2">ℹ️ Les admins ont toujours accès.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditLecteurs(null)}>Annuler</Button>
          <Button className="btn-marron" onClick={majLecteurs}>Enregistrer</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!toDelete}
        title="Supprimer le rapport ?"
        message={`« ${toDelete?.titre || ''} » — le lien ne fonctionnera plus.`}
        onConfirm={supprimer}
        onCancel={() => setToDelete(null)}
        confirmLabel="Supprimer"
      />
    </div>
  );
};

export default PointJourPage;
